import React, { useEffect, useState } from 'react';

import { Radio } from '@mui/material';
import dayjs from 'dayjs';
import { Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { MasterCodeForm, MonthYearForm } from './Forms';
import { Button } from '../../../components';
import { generateReport, getList, getReportData, removeReportData } from '../operations';

import './styles.scoped.css';

const AMCForm = ({
  masterCodeForm,
  monthYearForm,
  setMasterCodeForm,
  setMonthYearForm,
  isLoadingAmcCodes,
  isLoadingBranches,
  isLoadingPayCodes,
  isLoadingLOB,
  isMasterCode,
  setIsMasterCode,
  setIsLoadingBranches,
  setIsLoadingAmcCodes,
  setIsLoadingPayCodes,
  setIsLoadingLOB,
  setLoadingResults,
  setData,
  setShowResults,
  viewEnabled,
}) => {
  const navigate = useNavigate();
  const [years, setYears] = useState([]);
  const [branches, setBranches] = useState([]);
  const [amcCodes, setAmcCodes] = useState([]);
  const [payCodes, setPayCodes] = useState([]);
  const [LOB, setLOB] = useState([]);

  const handleGetBranches = async () => {
    setIsLoadingBranches(true);
    const newBranches = await getList('clients/branches');
    setBranches(newBranches);
    setIsLoadingBranches(false);
  };

  const handleGetAmcCodes = async () => {
    setIsLoadingAmcCodes(true);
    const newAmcCodes = await getList('amcs');
    setAmcCodes(newAmcCodes);
    setIsLoadingAmcCodes(false);
  };

  const handleGetPayCodes = async () => {
    setIsLoadingPayCodes(true);
    const newPayCodes = await getList('pay-codes');
    setPayCodes(newPayCodes);
    setIsLoadingPayCodes(false);
  };

  const handleGetLOB = async () => {
    setIsLoadingLOB(true);
    const newLOB = await getList('line-of-business');
    setLOB(newLOB);
    setIsLoadingLOB(false);
  };

  const handleSetYears = () => {
    const currentYear = dayjs().year();
    const yearsArray = [];
    for (let year = 2000; year <= currentYear; year++) {
      yearsArray.push(year);
    }
    setYears(yearsArray);
  };

  useEffect(() => {
    handleSetYears();
    handleGetBranches();
    handleGetAmcCodes();
    handleGetPayCodes();
    handleGetLOB();
  }, []);

  const yearOptions = years.map(year => ({
    label: year.toString(),
    value: year.toString(),
  }));

  const branchOptions = branches.map(({ Branch_Code, Branch_Name }) => ({
    label: `${Branch_Name} - ${Branch_Code}`,
    value: Branch_Code,
  }));

  const amcOptions = amcCodes.map(({ Code, Description }) => ({
    label: Description,
    value: Code,
  }));

  const payCodeOptions = payCodes.map(({ Pay_Code, Pay_Code_Description }) => ({
    label: Pay_Code_Description,
    value: Pay_Code,
  }));
  const lobOptions = LOB.map(({ LineOfBusinessCode, LineOfBusinessDesc }) => ({
    label: LineOfBusinessDesc,
    value: LineOfBusinessCode,
  }));

  const handleViewResults = async () => {
    setLoadingResults(true);

    try {
      const response = isMasterCode
        ? await generateReport({
            endpoint: 'client-billing-report-amc',
            payload: masterCodeForm,
          })
        : await generateReport({
            endpoint: 'client-billing-report-amc-month-year',
            payload: monthYearForm,
          });
      if (response.location && response.location !== '') {
        const reportData = await getReportData(response.location);
        if (reportData?.length) {
          await removeReportData(response.location);
          setLoadingResults(false);
          setData(reportData);
          setShowResults(true);
        }
      } else {
        setLoadingResults(false);
        alert('No data found matching your criteria.');
      }
    } catch (error) {
      console.log(error);
      setLoadingResults(false);
      alert('No data found matching your criteria.');
    }
  };

  const handleChange = e => {
    if (isMasterCode) {
      const _form = { ...masterCodeForm, [e.target.name]: e.target.value };
      setMasterCodeForm(_form);
    } else {
      const _form = { ...monthYearForm, [e.target.name]: e.target.value };
      setMonthYearForm(_form);
    }
  };
  return (
    <Form
      noValidate
      onSubmit={() => {}}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <p style={{ marginTop: '20px' }}>Select to search by AMC Code or AMC by Month/Year</p>
      <div className="radio-container">
        <div className="radio-group">
          <Radio
            style={{
              transform: 'scale(1)',
            }}
            className="radio-button"
            onChange={() => setIsMasterCode(true)}
            checked={isMasterCode}
          />
          <span>Acct Master Code</span>
        </div>
        <div className="radio-group">
          <Radio
            style={{
              transform: 'scale(1)',
            }}
            className="radio-button"
            onChange={() => setIsMasterCode(false)}
            checked={!isMasterCode}
          />
          <span>AMC Month/Year</span>
        </div>
      </div>
      {isMasterCode ? (
        <MasterCodeForm
          form={masterCodeForm}
          setForm={setMasterCodeForm}
          handleChange={handleChange}
          amcOptions={amcOptions}
          payCodeOptions={payCodeOptions}
          isLoadingAmcCodes={isLoadingAmcCodes}
          isLoadingPayCodes={isLoadingPayCodes}
        />
      ) : (
        <MonthYearForm
          yearOptions={yearOptions}
          amcOptions={amcOptions}
          payCodeOptions={payCodeOptions}
          branchOptions={branchOptions}
          lobOptions={lobOptions}
          isLoadingLOB={isLoadingLOB}
          isLoadingAmcCodes={isLoadingAmcCodes}
          isLoadingPayCodes={isLoadingPayCodes}
          form={monthYearForm}
          setForm={setMonthYearForm}
          isLoadingBranches={isLoadingBranches}
          handleChange={handleChange}
        />
      )}
      <div className="buttons-row">
        <Button
          variant="primary"
          disabled={!viewEnabled}
          onPress={() => handleViewResults()}
          style={{ marginRight: '20px', width: '100px' }}
          text="View"
        />
        <Button
          style={{ width: '100px' }}
          onPress={() => navigate('/edge-reporting')}
          variant="outline"
          text="Cancel"
        />
      </div>
    </Form>
  );
};

export default AMCForm;
